import {
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  TextField,
  useTheme,
} from '@mui/material';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { useContext } from 'react';
import * as React from 'react';
import { makeStyles } from 'tss-react/mui';
import * as Yup from 'yup';
import { AppConstantsContext, UserContext } from '../../../../../context';
import { TranscriptionDialogContext } from '../../../../../context/TranscriptionDialogContext/TranscriptionDialogContext';
import { COMPOSER_BLACKLIST } from '../TranscriptionDialog.config';
import { JobType } from '../../../../../entities';

export interface ComposerTitleProps {
  onSubmit: (useTickets: boolean) => void;
  hideTerms?: boolean;
  disableComposerField?: boolean;
}
const useStyles = makeStyles()(() => ({
  form: {
    width: '100%',
  },
  buttons: {
    display: 'flex',
    gap: 10,
    justifyContent: 'end',
  },
}));

const ComposerTitle = ({ hideTerms, disableComposerField, onSubmit }: ComposerTitleProps) => {
  const { t } = useTranslation();
  const { apriori, setApriori } = useContext(TranscriptionDialogContext);
  const { hasTicketsForUse } = useContext(UserContext);
  const { jobType } = useContext(AppConstantsContext);
  const { classes } = useStyles();
  const theme = useTheme();
  const validationSchema = Yup.object({
    title: Yup.string().min(3, 'Min. 3 character').required('This field is required'),
    composer: Yup.string()
      .min(3, 'Min. 3 character')
      .required('This field is required')
      .notOneOf(
        COMPOSER_BLACKLIST,
        t('home.input.dialog.title_composer.composer_error_message', {
          composer: apriori.composer,
        }),
      ),
  });

  const formik = useFormik({
    initialValues: {
      title: apriori.title ?? '',
      composer: apriori.composer ?? '',
      agreedToTerms: apriori.agreedToTerms,
      useTickets: false,
    },
    validationSchema,
    onSubmit: (values) => {
      onSubmit(values.useTickets);
    },
  });

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    formik.handleChange(e);
    setApriori({
      [e.target.name]: e.target.value,
    });
  };

  const onChangeCheckbox = (e: React.ChangeEvent<HTMLInputElement>) => {
    formik.handleChange(e);
    setApriori({
      [e.target.name]: e.target.checked,
    });
  };
  return (
    <form onSubmit={formik.handleSubmit} className={classes.form}>
      <TextField
        id="title"
        name="title"
        label={t('home.input.dialog.title_composer.piece_title_label')}
        variant="outlined"
        margin="normal"
        fullWidth
        type="text"
        onChange={onChange}
        inputProps={{ style: { background: 'none' } }}
        error={Boolean(formik.touched.title && formik.errors.title)}
        helperText={formik.touched.title && formik.errors.title}
        value={formik.values.title}
      />
      <TextField
        id="composer"
        name="composer"
        label={t('home.input.dialog.title_composer.enter_composer_label')}
        variant="outlined"
        margin="normal"
        fullWidth
        type="text"
        inputProps={{ style: { background: 'none' } }}
        error={Boolean(formik.touched.composer && formik.errors.composer)}
        onChange={onChange}
        helperText={formik.touched.composer && formik.errors.composer}
        disabled={disableComposerField}
        value={formik.values.composer}
      />

      {!hideTerms && (
        <FormControl required component="fieldset" error={!hideTerms}>
          <FormControlLabel
            label={(
              <p
                style={{ fontSize: 12, color: '#"ff' }}
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{
                  __html: t('home.input.dialog.title_composer.agree_legal_terms_text', {
                    style: `font-size: 12px; color: ${theme.palette.primary.main}`,
                    interpolation: { escapeValue: false },
                  }),
                }}
              />
            )}
            control={(
              <Checkbox
                onChange={onChangeCheckbox}
                color="primary"
                name="agreedToTerms"
                checked={formik.values.agreedToTerms}
              />
            )}
          />
          <FormHelperText
            style={{
              visibility:
                formik.values?.agreedToTerms || !formik.touched.agreedToTerms
                  ? 'hidden'
                  : undefined,
            }}
            aria-hidden={formik.values?.agreedToTerms ? 'true' : 'false'}
          >
            {t('home.input.dialog.title_composer.accept_terms_error')}
          </FormHelperText>
        </FormControl>
      )}
      <div className={classes.buttons}>
        <Button
          id="btn_wizard_submit_demo"
          data-testid="composer-title-continue-btn-demo"
          size="small"
          disabled={!formik.values?.agreedToTerms || formik.isSubmitting}
          name="submit_demo"
          variant="contained"
          type="submit"
          value="submit_demo"
        >
          {t('home.input.dialog.start_transcription')}
        </Button>
        {hasTicketsForUse && (
          <Button
            id="btn_wizard_submit"
            name="useTickets"
            data-testid="composer-title-continue-btn"
            size="small"
            disabled={!formik.values?.agreedToTerms || formik.isSubmitting}
            variant="contained"
            onClick={() => {
              formik.setFieldValue('useTickets', true);
              formik.handleSubmit();
            }}
            type="submit"
            value="submit_useTickets"
          >
            {jobType === JobType.TranscribeScan
              ? t('home.input.dialog.start_scan_use_tickets')
              : t('home.input.dialog.start_transcription_use_tickets')}
          </Button>
        )}
      </div>
    </form>
  );
};

export default ComposerTitle;
